const W = 320, H = 180, PAD_L = 40, PAD_R = 10, PAD_T = 14, PAD_B = 24;

export const money = (n, cur = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: cur, maximumFractionDigits: n % 1 ? 2 : 0 }).format(n || 0);

function niceTicks(max) {
  const raw = Math.max(max, 1) / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw);
  return [0, 1, 2, 3, 4].map((i) => i * step);
}

const tickLabel = (v, sym) => (v >= 1000 ? `${sym}${+(v / 1000).toFixed(1)}k` : `${sym}${v}`);

// Catmull-Rom → cubic Bézier for a smooth line
function smoothPath(pts) {
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C ${c1[0]} ${c1[1]}, ${c2[0]} ${c2[1]}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

export function BalanceChart({ series, currency = "GBP" }) {
  const sym = currency === "GBP" ? "£" : "$";
  const ticks = niceTicks(Math.max(...series.map((p) => p.value)));
  const yMax = ticks[4] * 1.08;
  const x = (i) => PAD_L + (i * (W - PAD_L - PAD_R)) / (series.length - 1);
  const y = (v) => PAD_T + (1 - v / yMax) * (H - PAD_T - PAD_B);
  const pts = series.map((p, i) => [x(i), y(p.value)]);
  const line = smoothPath(pts);
  const area = `${line} L ${pts[pts.length - 1][0]} ${y(0)} L ${pts[0][0]} ${y(0)} Z`;
  const last = pts[pts.length - 1];
  const lastVal = series[series.length - 1].value;
  return (
    <svg data-testid="balance-chart" viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={`Running balance, now ${money(lastVal, currency)}`}>
      <defs>
        <linearGradient id="bal-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
        </linearGradient>
      </defs>
      {ticks.map((t, i) => (
        <g key={t}>
          <line x1={PAD_L} x2={W - PAD_R} y1={y(t)} y2={y(t)} stroke="#2A2A30" strokeDasharray="3 4" />
          <text data-testid={`y-tick-${i}`} x={PAD_L - 6} y={y(t) + 3.5} textAnchor="end" fontSize="9.5" fill="#71717A">{tickLabel(t, sym)}</text>
        </g>
      ))}
      {series.map((p, i) => (
        <text key={p.key || p.month} x={x(i)} y={H - 6} textAnchor="middle" fontSize="9.5" fill="#71717A">{p.month}</text>
      ))}
      <path d={area} fill="url(#bal-fill)" />
      <path d={line} fill="none" stroke="#3B82F6" strokeWidth="2.5" strokeLinecap="round" className="ac-draw" />
      <circle cx={last[0]} cy={last[1]} r="4" fill="#3B82F6" stroke="#1A1A1E" strokeWidth="2" />
      <text data-testid="balance-chart-last" x={last[0] - 6} y={last[1] - 8} textAnchor="end" fontSize="10" fontWeight="700" fill="#E4E4E7">{money(lastVal, currency)}</text>
    </svg>
  );
}
